import React from 'react';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Plus, Trash2, Users } from 'lucide-react';

const DEFAULT_TASKS = [
  { name: 'Review 3MF/STL', minutes: 10 },
  { name: 'Start Print', minutes: 1 },
  { name: 'Change Filament', minutes: 1 },
  { name: 'Clean Nozzle', minutes: 0.5 },
  { name: 'Spray Build Plate', minutes: 0.5 },
  { name: 'Remove Print', minutes: 0.5 },
];

export const LabourPanel = ({ tasks, onChange }) => {
  const handleAdd = () => {
    onChange([...tasks, { name: 'New Task', minutes: 1 }]);
  };

  const handleRemove = (index) => {
    onChange(tasks.filter((_, i) => i !== index));
  };

  const handleUpdate = (index, field, value) => {
    const updated = [...tasks];
    updated[index][field] = field === 'minutes' ? parseFloat(value) : value;
    onChange(updated);
  };

  const handleReset = () => {
    onChange(DEFAULT_TASKS);
  };

  const totalMinutes = tasks.reduce((sum, task) => sum + task.minutes, 0);

  return (
    <Card className="p-5 bg-white border-gray-200 shadow-md hover:shadow-lg transition-all">
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-orange-100">
              <Users className="h-4 w-4 text-orange-600" />
            </div>
            <h3 className="font-semibold text-gray-800">Labour Tasks</h3>
          </div>
          <Button
            data-testid="reset-tasks-btn"
            size="sm"
            variant="outline"
            onClick={handleReset}
            className="text-xs border-gray-300"
          >
            Reset
          </Button>
        </div>

        <div className="space-y-2">
          {tasks.map((task, index) => (
            <div
              key={index}
              className="flex items-center gap-2 p-2 bg-gray-50 rounded border border-gray-200"
            >
              <div className="flex-1 grid grid-cols-2 gap-2">
                <Input
                  data-testid={`task-name-${index}`}
                  value={task.name}
                  onChange={(e) => handleUpdate(index, 'name', e.target.value)}
                  className="bg-white border-gray-300 h-8 text-xs"
                  placeholder="Task name"
                />
                <Input
                  data-testid={`task-minutes-${index}`}
                  type="number"
                  value={task.minutes}
                  onChange={(e) => handleUpdate(index, 'minutes', e.target.value)}
                  className="bg-white border-gray-300 h-8 text-xs"
                  placeholder="Minutes"
                  step="0.5"
                  min="0"
                />
              </div>
              <Button
                data-testid={`remove-task-${index}`}
                size="sm"
                variant="ghost"
                onClick={() => handleRemove(index)}
                className="h-6 w-6 p-0 hover:bg-red-50 hover:text-red-600"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>

        <Button
          data-testid="add-task-btn"
          size="sm"
          onClick={handleAdd}
          variant="outline"
          className="w-full border-gray-300"
        >
          <Plus className="h-4 w-4 mr-1" />
          Add Task
        </Button>

        <Separator className="bg-gray-200" />

        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Total Time:</span>
          <span className="font-mono text-gray-800 font-semibold">
            {totalMinutes.toFixed(1)} min ({(totalMinutes / 60).toFixed(2)} hrs)
          </span>
        </div>
      </div>
    </Card>
  );
};